import { isArray, isNonNullish, isObjectType } from 'remeda'

/** Narrow an unknown to an indexable object, or undefined. */
const asRecord = (value: unknown): Record<string, unknown> | undefined =>
  isObjectType(value) && isNonNullish(value)
    ? (value as Record<string, unknown>)
    : undefined

/**
 * Read a property path over unknown data, short-circuiting to `undefined`
 * at the first segment that is not a non-null object.
 */
export const readPath = (value: unknown, path: readonly string[]): unknown =>
  path.reduce<unknown>((acc, key) => asRecord(acc)?.[key], value)

/** True when the value at `path` is an array. */
export const hasArrayPath = (
  value: unknown,
  path: readonly string[]
): boolean => isArray(readPath(value, path))

/**
 * Read a property path and return it as a typed array.
 * - Returns `[]` when the path is missing or the value is not an array.
 * - With `itemGuard`, elements are filtered (fully type-safe, no assertion).
 * - Without `itemGuard`, the array is asserted to `readonly T[]` — only use
 *   when the element shape is guaranteed by the backend contract.
 */
export const readArrayPath = <T>(
  value: unknown,
  path: readonly string[],
  itemGuard?: (item: unknown) => item is T
): readonly T[] => {
  const result = readPath(value, path)

  if (!isArray(result)) {
    return []
  }

  return itemGuard ? result.filter(itemGuard) : (result as readonly T[])
}
